import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { ComponentProps, FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBox } from 'record-it-components/misc/MessageBox/MessageBox';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import {
  LatLong,
  LocationUtils
} from 'record-it-utils/location/LocationUtils/LocationUtils';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { ConstructionSiteToSelectListItem } from '../ConstructionSiteToSelectListItem/ConstructionSiteToSelectListItem';

export type NearbyConstructionSitesToSelectListProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  constructionSites: Array<ConstructionSite>;
  currentLocation: LatLong;
  onConstructionSiteSelected: (constructionSite: ConstructionSite) => void;
};

export const NearbyConstructionSitesToSelectList: FunctionComponent<NearbyConstructionSitesToSelectListProps> =
  React.memo((props) => {
    const {
      currentLocation,
      constructionSites,
      onConstructionSiteSelected,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const displayedConstructionSitesWithDistance = useMemo<
      Array<ConstructionSiteWithDistance>
    >(() => {
      const constructionSitesWithDistance = getConstructionSitesWithDistance(
        constructionSites,
        currentLocation
      );

      constructionSitesWithDistance.sort((a, b) => a.distance - b.distance);

      return constructionSitesWithDistance.slice(0, 5);
    }, [constructionSites, currentLocation]);

    return (
      <div {...remainingProps}>
        {displayedConstructionSitesWithDistance.map(({ constructionSite }) => (
          <ConstructionSiteToSelectListItem
            key={EntityUtils.getTrackingKey(constructionSite)}
            constructionSite={constructionSite}
            onConstructionSiteSelected={onConstructionSiteSelected}
            data-testid="NearbyConstructionSitesToSelectList.ListItem"
          ></ConstructionSiteToSelectListItem>
        ))}

        {displayedConstructionSitesWithDistance.length === 0 ? (
          <MessageBox data-testid="NearbyConstructionSitesToSelectList.NoNearbyConstructionSites">
            {t(
              'components.constructionSite.NearbyConstructionSitesToSelectList.noNearbyConstructionSites'
            )}
          </MessageBox>
        ) : null}
      </div>
    );
  });

function getConstructionSitesWithDistance(
  constructionSites: Array<ConstructionSite>,
  currentLocation: LatLong
): Array<ConstructionSiteWithDistance> {
  const constructionSitesWithDistance: Array<ConstructionSiteWithDistance> = [];

  for (const constructionSite of constructionSites) {
    if (!constructionSite.location) {
      continue;
    }

    constructionSitesWithDistance.push({
      constructionSite,
      distance: LocationUtils.distanceBetweenCoordinates(
        constructionSite.location,
        currentLocation
      )
    });
  }

  return constructionSitesWithDistance;
}

type ConstructionSiteWithDistance = {
  constructionSite: ConstructionSite;
  distance: number;
};
